var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useState, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { Loader } from '@ui';
import { Table } from '@ui/molecules/Table';
import { aiInspectionManagerPath } from '@routes';
import { previewResolvedConfig, searchBuildings, searchTechnicians } from '../services/TkbService';
import AppliedFromTag from './AppliedFromTag';
/**
 * Preview tab — UX rebuild (Ken review 2026-06-12).
 *
 * Layout:
 *   workspace__section  (intro + building picker row + technician picker row)
 *   workspace__section  (resolved rules table)  — only when results loaded
 *   workspace__section  (check overrides table) — only when results loaded
 *
 * - Building selected via async name+address typeahead; Run button aligned in
 *   the same row at the same height as the input.
 * - Technician (optional) adds an actor filter to see only rules that apply to
 *   a specific technician (technician-scoped rules feature, item 1.6).
 * - No raw ID inputs; "Resolved for: <building name>" shown after a run.
 * - Check matrix is read-only with a cross-link to Inspection Assistant Settings.
 */
export default function PreviewPanel({ issueTypes, t }) {
    var _a;
    const [buildingQuery, setBuildingQuery] = useState('');
    const [buildingOptions, setBuildingOptions] = useState([]);
    const [buildingLoading, setBuildingLoading] = useState(false);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [technicianQuery, setTechnicianQuery] = useState('');
    const [technicianOptions, setTechnicianOptions] = useState([]);
    const [technicianLoading, setTechnicianLoading] = useState(false);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const techSearchSeqRef = useRef(0);
    const issueTypeName = (value) => { var _a, _b; return (_b = (_a = issueTypes.find((it) => it.value === value)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : String(value); };
    const preview = useMutation({
        mutationFn: (params) => previewResolvedConfig(params)
    });
    const { reset: resetPreview, data, isLoading: previewLoading, isError: previewIsError, error: previewError } = preview;
    const handleBuildingSearch = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        setBuildingQuery(query);
        setSelectedBuilding(null);
        resetPreview();
        if (query.length < 2) {
            setBuildingOptions([]);
            return;
        }
        setBuildingLoading(true);
        try {
            const results = yield searchBuildings(query);
            setBuildingOptions(results);
        }
        catch (_a) {
            setBuildingOptions([]);
        }
        finally {
            setBuildingLoading(false);
        }
    }), [resetPreview]);
    const handleSelectBuilding = (opt) => {
        setSelectedBuilding(opt);
        setBuildingQuery(opt.label);
        setBuildingOptions([]);
        resetPreview();
    };
    const handleTechnicianSearch = useCallback((query) => __awaiter(this, void 0, void 0, function* () {
        setTechnicianQuery(query);
        setSelectedTechnician(null);
        resetPreview();
        if (query.length < 2) {
            setTechnicianOptions([]);
            return;
        }
        setTechnicianLoading(true);
        techSearchSeqRef.current += 1;
        const seq = techSearchSeqRef.current;
        try {
            const results = yield searchTechnicians(query);
            if (seq === techSearchSeqRef.current) {
                setTechnicianOptions(results);
            }
        }
        catch (_a) {
            if (seq === techSearchSeqRef.current) {
                setTechnicianOptions([]);
            }
        }
        finally {
            if (seq === techSearchSeqRef.current) {
                setTechnicianLoading(false);
            }
        }
    }), [resetPreview]);
    const handleSelectTechnician = (opt) => {
        setSelectedTechnician(opt);
        setTechnicianQuery(opt.label);
        setTechnicianOptions([]);
        resetPreview();
    };
    const handleClearTechnician = () => {
        setSelectedTechnician(null);
        setTechnicianQuery('');
        setTechnicianOptions([]);
        resetPreview();
    };
    const handleRun = () => {
        if (selectedBuilding || selectedTechnician) {
            preview.mutate({
                buildingId: selectedBuilding === null || selectedBuilding === void 0 ? void 0 : selectedBuilding.value,
                technicianId: selectedTechnician === null || selectedTechnician === void 0 ? void 0 : selectedTechnician.value
            });
        }
    };
    // resetPreview() clears mutation state but does not cancel an in-flight request.
    // Stale-check: ignore results whose building_id or technician_id no longer match
    // the current selection so a slower response cannot paint wrong rules.
    const activePreview = (() => {
        var _a, _b, _c, _d;
        if (!data)
            return undefined;
        if (!selectedBuilding && !selectedTechnician)
            return undefined;
        if (((_a = data.scope_context.building_id) !== null && _a !== void 0 ? _a : null) !== ((_b = selectedBuilding === null || selectedBuilding === void 0 ? void 0 : selectedBuilding.value) !== null && _b !== void 0 ? _b : null))
            return undefined;
        if (((_c = data.scope_context.technician_id) !== null && _c !== void 0 ? _c : null) !== ((_d = selectedTechnician === null || selectedTechnician === void 0 ? void 0 : selectedTechnician.value) !== null && _d !== void 0 ? _d : null))
            return undefined;
        return data;
    })();
    // IP-14861 — "Rules in Effect" detail table: Rule + Applied From (provenance tag).
    const ruleColumns = [
        {
            dataKey: 'directive_human',
            label: t('rules.headers.rule'),
            columnType: 'primary',
            renderer: ({ item }) => (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "tkb-preview__rule-directive" }, item.directive_human),
                item.rule_kind && (React.createElement("div", { className: "tkb-preview__rule-kind" }, t('preview.rule_kind_meta', { kind: item.rule_kind })))))
        },
        {
            dataKey: 'applied_from',
            label: t('preview.applied_from_header'),
            width: '220px',
            columnType: 'last',
            renderer: ({ item }) => React.createElement(AppliedFromTag, { scope: item.scope_level, t: t })
        }
    ];
    // IP-14861 — "Inspection Assistant Checks in Effect": enabled final-state checks.
    const checkColumns = [
        {
            dataKey: 'check',
            label: t('preview.checks_header'),
            columnType: 'primary',
            renderer: ({ item }) => React.createElement(React.Fragment, null, issueTypeName(item.issue_type))
        },
        {
            dataKey: 'applied_from',
            label: t('preview.applied_from_header'),
            width: '220px',
            columnType: 'last',
            renderer: ({ item }) => React.createElement(AppliedFromTag, { scope: item.resolved_from, t: t })
        }
    ];
    // Checks tab shows ENABLED final-state checks (not overrides) — filter to enabled.
    const enabledChecks = ((_a = activePreview === null || activePreview === void 0 ? void 0 : activePreview.check_config) !== null && _a !== void 0 ? _a : []).filter((cc) => cc.enabled);
    // Cap the inline tag list — buildings can carry dozens of tags, which turned
    // this context line into an unreadable wall of text. Show the first few and
    // summarize the remainder as "+N more".
    const MAX_PREVIEW_TAGS = 6;
    const resolvedForText = () => {
        if (!activePreview || !selectedBuilding)
            return '';
        const buildingName = selectedBuilding.label;
        const tags = activePreview.scope_context.building_tags;
        if (tags && tags.length > 0) {
            const shown = tags.slice(0, MAX_PREVIEW_TAGS).join(', ');
            const tagsStr = tags.length > MAX_PREVIEW_TAGS
                ? `${shown} ${t('preview.resolved_for_tags_more', { count: tags.length - MAX_PREVIEW_TAGS })}`
                : shown;
            return t('preview.resolved_for_with_tags', { building: buildingName, tags: tagsStr });
        }
        return t('preview.resolved_for', { building: buildingName });
    };
    const resolvedForHeading = () => {
        if (!activePreview)
            return '';
        if (selectedBuilding && selectedTechnician) {
            return t('preview.resolved_for_heading_with_technician', {
                building: selectedBuilding.label,
                technician: selectedTechnician.label
            });
        }
        if (selectedBuilding) {
            return t('preview.resolved_for_heading', { building: selectedBuilding.label });
        }
        if (selectedTechnician) {
            return t('preview.resolved_for_heading_technician_only', { technician: selectedTechnician.label });
        }
        return '';
    };
    const noRulesKey = selectedTechnician && !selectedBuilding ? 'preview.no_rules_technician_only' : 'preview.no_rules';
    return (React.createElement("div", { "data-testid": "preview-panel" },
        React.createElement("section", { className: "workspace__section" },
            React.createElement("div", { className: "tkb-preview__section-body" },
                React.createElement(Text, { variant: "body", as: "p", style: { marginBottom: '16px', color: 'var(--color-text-secondary, #555)' } }, t('preview.intro')),
                React.createElement("div", { className: "tkb-preview__picker-row" },
                    React.createElement("div", { className: "tkb-preview__picker-field" },
                        React.createElement("label", { className: "qmb-label", htmlFor: "preview-building-search" }, t('preview.building_label')),
                        React.createElement("div", { className: "tkb-preview__typeahead-wrap" },
                            React.createElement("div", { className: "qmb-input--search tkb-preview__search-input" },
                                React.createElement("input", { id: "preview-building-search", type: "text", placeholder: t('preview.building_search_placeholder'), value: buildingQuery, onChange: (e) => {
                                        void handleBuildingSearch(e.target.value);
                                    }, autoComplete: "off" }),
                                buildingLoading ? React.createElement(Loader, { size: "16" }) : React.createElement("i", { className: "fa-light fa-search", "aria-hidden": "true" })),
                            buildingOptions.length > 0 && (React.createElement("ul", { className: "tkb-preview__building-results" }, buildingOptions.map((opt) => (React.createElement("li", { key: opt.value },
                                React.createElement("button", { type: "button", className: "tkb-preview__building-option", onClick: () => handleSelectBuilding(opt) }, opt.label)))))))),
                    React.createElement("div", { className: "tkb-preview__picker-action" },
                        React.createElement(Button, { variant: "primary", disabled: (!selectedBuilding && !selectedTechnician) || previewLoading, onClick: handleRun }, previewLoading ? t('preview.running') : t('preview.run')))),
                React.createElement("div", { className: "tkb-preview__picker-row", style: { marginTop: '12px' } },
                    React.createElement("div", { className: "tkb-preview__picker-field" },
                        React.createElement("label", { className: "qmb-label", htmlFor: "preview-technician-search" }, t('preview.technician_label')),
                        React.createElement("div", { className: "tkb-preview__typeahead-wrap" },
                            React.createElement("div", { className: "qmb-input--search tkb-preview__search-input" },
                                React.createElement("input", { id: "preview-technician-search", type: "text", placeholder: t('preview.technician_search_placeholder'), value: technicianQuery, onChange: (e) => {
                                        void handleTechnicianSearch(e.target.value);
                                    }, autoComplete: "off" }),
                                technicianLoading ? React.createElement(Loader, { size: "16" }) : React.createElement("i", { className: "fa-light fa-search", "aria-hidden": "true" })),
                            technicianOptions.length > 0 && (React.createElement("ul", { className: "tkb-preview__building-results" }, technicianOptions.map((opt) => (React.createElement("li", { key: opt.value },
                                React.createElement("button", { type: "button", className: "tkb-preview__building-option", onClick: () => handleSelectTechnician(opt) }, opt.label)))))))),
                    selectedTechnician && (React.createElement("div", { className: "tkb-preview__picker-action" },
                        React.createElement(Button, { variant: "secondary", onClick: handleClearTechnician }, t('preview.technician_clear'))))),
                activePreview && (selectedBuilding || selectedTechnician) && (React.createElement("header", { className: "section__title tkb-preview__settings-heading" },
                    React.createElement("h2", { className: "section__label" }, resolvedForHeading()),
                    React.createElement("hr", { className: "section__split" }))),
                previewIsError && (React.createElement(Text, { style: { color: 'var(--color-danger, #c0392b)', marginTop: '12px' } }, (previewError === null || previewError === void 0 ? void 0 : previewError.message) || t('preview.error'))))),
        previewLoading && (React.createElement("div", { className: "loading-center" },
            React.createElement(Loader, { size: "32" }))),
        activePreview && (React.createElement(React.Fragment, null,
            React.createElement("section", { className: "workspace__section", "data-testid": "preview-results" },
                React.createElement("header", { className: "section__title" },
                    React.createElement("h2", { className: "section__label" }, t('preview.rules_heading')),
                    React.createElement("hr", { className: "section__split" })),
                resolvedForText() && React.createElement("p", { className: "tkb-preview__resolved-for" }, resolvedForText()),
                React.createElement("div", { className: "tkb-preview__section-body" },
                    React.createElement(Text, { variant: "small", as: "p", style: { color: 'var(--color-text-secondary, #555)', marginBottom: '12px' } }, t('preview.rules_helper')),
                    activePreview.rules.length === 0 ? (React.createElement("p", { className: "no-data-message" }, t(noRulesKey))) : (React.createElement(Table, { variant: "detail", columns: ruleColumns, data: activePreview.rules, getItemId: (item) => item.id })))),
            React.createElement("section", { className: "workspace__section" },
                React.createElement("header", { className: "section__title" },
                    React.createElement("h2", { className: "section__label" }, t('preview.checks_heading')),
                    React.createElement("hr", { className: "section__split" }),
                    React.createElement("div", { className: "section__actions" },
                        React.createElement("a", { href: aiInspectionManagerPath(), className: "qmb-ui-button", "data-testid": "preview-manage-exclusions" },
                            React.createElement("i", { className: "fa-light fa-gear", "aria-hidden": "true" }),
                            t('preview.checks_edit_link')))),
                React.createElement("div", { className: "tkb-preview__section-body" },
                    React.createElement(Text, { variant: "small", as: "p", style: { color: 'var(--color-text-secondary, #555)', marginBottom: '12px' } }, t('preview.checks_helper')),
                    enabledChecks.length === 0 ? (React.createElement("p", { className: "no-data-message" }, t('preview.no_check_overrides'))) : (React.createElement(Table, { variant: "detail", columns: checkColumns, data: enabledChecks, getItemId: (item) => item.issue_type }))))))));
}
